import React, { useMemo } from 'react'
import { Command } from 'cmdk'
import CmdKGroup from '../CmdKGroup'
import CommandItem from '../CmdKItem'
import { useQuery, gql } from '@apollo/client'
import { useOrgName } from 'src/hooks/useOrgName'
import { navigate, routes } from '@cedarjs/router'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import FlightRoundedIcon from '@mui/icons-material/FlightRounded'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import { filter } from '../filter'

const COMPLIANCE_LEDGERS_QUERY = gql`
  query ComplianceLedgers($orgSlug: String!) {
    complianceLedgers(
      complianceLedgerFilters: {
        orgSlug: $orgSlug
        statuses: [IN_PROGRESS, IN_REVIEW, REVISION_IN_PROGRESS]
      }
    ) {
      id
      name
      customName
      status
      aircraft {
        id
        tailNumber
      }
    }
  }
`

interface CommandMenuGroup {
  heading: string
  items: {
    label: string
    icon?: React.ReactNode
    subtext?: string
    disabled?: boolean
    onClick?: () => void
    children?: {
      label: string
      icon?: React.ReactNode
      subtext?: string
      onClick?: () => void
    }[]
  }[]
}

interface ComplianceModuleProps {
  search: string
  onNavigate: (groups: CommandMenuGroup[]) => void
  onBack: () => void
  pages: CommandMenuGroup[][]
  onClose: () => void
}

const ComplianceModule: React.FC<ComplianceModuleProps> = ({
  onNavigate,
  pages,
  search,
  onClose,
}) => {
  const orgName = useOrgName()
  const { data, loading } = useQuery(COMPLIANCE_LEDGERS_QUERY, {
    variables: { orgSlug: orgName },
  })

  const handleComplianceClick = () => {
    onNavigate([
      {
        heading: 'Compliance',
        items: complianceItems,
      },
    ])
  }

  const complianceItems = useMemo(() => {
    const items = []

    if (data?.complianceLedgers) {
      items.push(
        ...data.complianceLedgers.map((ledger) => ({
          label: `${ledger.aircraft.tailNumber} - ${
            ledger.customName || ledger.name || 'Compliance'
          }`,
          subtext: 'Compliance',
          keywords: [
            ledger.aircraft.tailNumber,
            ledger.customName || ledger.name || 'compliance',
            ledger.status.toLowerCase(),
            'bulk',
            'compliance',
          ],
          startIcon: <AssignmentOutlinedIcon />,
          onClick: () => {
            onClose()
            navigate(routes.bulkCompliance({ orgName, ledgerId: ledger.id }))
          },
        }))
      )
    }

    return items
  }, [data?.complianceLedgers, orgName, onClose])

  // Only show the top-level item when we're at root level
  if (pages.length > 0) {
    return null
  }

  const complianceCount = data?.complianceLedgers?.length || 0

  const filteredComplianceItems = complianceItems.filter((item) =>
    filter(item.label, search, item.keywords)
  )

  if (filteredComplianceItems.length === 0) {
    return null
  }

  // If there's a search term, show items (or loading state)
  if (search) {
    if (data?.complianceLedgers) {
      return (
        <CmdKGroup heading="Compliance">
          {complianceItems.map((item, index) => (
            <CommandItem
              key={index}
              onSelect={item.onClick}
              startIcon={item.startIcon}
              subtext={item.subtext}
              keywords={item.keywords}
            >
              {item.label}
            </CommandItem>
          ))}
        </CmdKGroup>
      )
    }
  }

  // Show parent item if no search term
  return (
    <CmdKGroup heading="Compliance">
      <CommandItem
        onSelect={handleComplianceClick}
        startIcon={<AssignmentOutlinedIcon />}
        subtext={`${complianceCount} active compliance ledgers`}
      >
        Compliance
      </CommandItem>
    </CmdKGroup>
  )
}

export default ComplianceModule
